import { defineComponent, h, ref, computed, onMounted, type Ref } from 'vue'
import { useRouter as vueUseRouter, useRoute as vueUseRoute, type Router } from 'vue-router'

type FetchOptions = {
  method?: string
  body?: unknown
  /** ofetch / Nuxt：$fetch 查询参数 */
  query?: Record<string, unknown>
  /** ofetch 别名，与 query 等价（大量页面用 params） */
  params?: Record<string, unknown>
  headers?: Record<string, string>
  credentials?: RequestCredentials
}

function buildUrl(url: string, query?: Record<string, unknown>): string {
  if (!query) return url
  const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost')
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue
    if (Array.isArray(v)) {
      for (const item of v) {
        if (item === undefined || item === null) continue
        u.searchParams.append(k, String(item))
      }
      continue
    }
    u.searchParams.set(k, String(v))
  }
  return u.pathname + u.search
}

export async function $fetch<T = any>(url: string, opts: FetchOptions = {}): Promise<T> {
  const method = (opts.method || 'GET').toUpperCase()
  const query = { ...(opts.query || {}), ...(opts.params || {}) }
  const finalUrl = buildUrl(url, Object.keys(query).length ? query : undefined)
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(opts.headers || {}),
  }
  let body: BodyInit | undefined
  if (opts.body !== undefined && method !== 'GET' && method !== 'HEAD') {
    if (opts.body instanceof FormData) {
      body = opts.body
    } else {
      headers['Content-Type'] = headers['Content-Type'] || 'application/json'
      body = JSON.stringify(opts.body)
    }
  }
  const res = await fetch(finalUrl, {
    method,
    headers,
    body,
    credentials: opts.credentials || 'include',
  })
  const text = await res.text()
  let data: any = null
  try {
    data = text ? JSON.parse(text) : null
  } catch {
    data = text
  }
  if (!res.ok) {
    const err: any = new Error(data?.message || res.statusText || '请求失败')
    err.statusCode = res.status
    err.data = data
    throw err
  }
  return data as T
}

$fetch.raw = async (url: string, opts: FetchOptions = {}) => {
  const data = await $fetch(url, opts)
  return { _data: data, data }
}

let appRouter: Router | null = null

export function setAppRouter(router: Router) {
  appRouter = router
}

export function navigateTo(to: string | { path?: string; query?: Record<string, any> }) {
  const router = appRouter
  if (!router) {
    const path = typeof to === 'string' ? to : to.path || '/'
    window.location.assign(path)
    return Promise.resolve()
  }
  if (typeof to === 'string') return router.push(to)
  return router.push(to)
}

export function useRuntimeConfig() {
  return {
    public: {
      apiBase: import.meta.env.VITE_API_BASE || '',
      opsCenterUrl: import.meta.env.VITE_OPS_CENTER_URL || '/ops-center/',
      promptFactoryUrl: import.meta.env.VITE_PROMPT_FACTORY_URL || '/prompt-factory/',
    },
  }
}

export function definePageMeta(_meta?: Record<string, unknown>) {
  // SPA 无 Nuxt page meta，noop
}

export function useHead(_input?: unknown) {
  // noop：SEO 对管理后台非必需
}

export function useCookie<T = string | null>(name: string, opts?: { default?: () => T }) {
  const read = (): T => {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
    if (!m) return (opts?.default ? opts.default() : null) as T
    try {
      return decodeURIComponent(m[1]!) as T
    } catch {
      return m[1] as T
    }
  }
  const state = ref(read()) as Ref<T>
  return computed({
    get: () => state.value,
    set: (v: T) => {
      state.value = v
      if (v == null) {
        document.cookie = `${name}=; Max-Age=0; path=/`
      } else {
        document.cookie = `${name}=${encodeURIComponent(String(v))}; path=/`
      }
    },
  })
}

export function useState<T>(key: string, init?: () => T): Ref<T> {
  const g = globalThis as any
  g.__touliuState = g.__touliuState || {}
  if (!(key in g.__touliuState)) {
    g.__touliuState[key] = ref(init ? init() : null)
  }
  return g.__touliuState[key]
}

export async function useFetch<T = any>(url: string, opts?: FetchOptions & { immediate?: boolean }) {
  const data = ref<T | null>(null)
  const error = ref<any>(null)
  const pending = ref(false)
  const refresh = async () => {
    pending.value = true
    error.value = null
    try {
      data.value = await $fetch<T>(url, opts)
    } catch (e) {
      error.value = e
    } finally {
      pending.value = false
    }
  }
  if (opts?.immediate !== false) await refresh()
  return { data, error, pending, refresh }
}

export async function useAsyncData<T>(key: string, handler: () => Promise<T>) {
  const data = ref<T | null>(null)
  const error = ref<any>(null)
  const pending = ref(true)
  try {
    data.value = await handler()
  } catch (e) {
    error.value = e
  } finally {
    pending.value = false
  }
  return { data, error, pending, refresh: async () => {
    pending.value = true
    try {
      data.value = await handler()
    } catch (e) {
      error.value = e
    } finally {
      pending.value = false
    }
  }}
}

export const ClientOnly = defineComponent({
  name: 'ClientOnly',
  setup(_, { slots }) {
    const show = ref(false)
    onMounted(() => { show.value = true })
    return () => (show.value ? slots.default?.() : slots.fallback?.())
  },
})

export const NuxtLink = defineComponent({
  name: 'NuxtLink',
  props: {
    to: { type: [String, Object], required: true },
  },
  setup(props, { slots }) {
    const router = vueUseRouter()
    return () =>
      h(
        'a',
        {
          href: typeof props.to === 'string' ? props.to : (props.to as any)?.path || '#',
          onClick: (e: Event) => {
            e.preventDefault()
            router.push(props.to as any)
          },
        },
        slots.default?.()
      )
  },
})

export function useRouter() {
  return vueUseRouter()
}

export function useRoute() {
  return vueUseRoute()
}

export function installNuxtCompat() {
  const g = globalThis as any
  g.$fetch = $fetch
  g.navigateTo = navigateTo
  g.useRuntimeConfig = useRuntimeConfig
  g.definePageMeta = definePageMeta
  g.useHead = useHead
  g.useCookie = useCookie
  g.useState = useState
  g.useFetch = useFetch
  g.useAsyncData = useAsyncData
  g.useRouter = useRouter
  g.useRoute = useRoute
}
